// Extract relevant properties from a KeyboardEvent into a plain object
// since KeyboardEvent properties are getters on the prototype and won't survive JSON.stringify
function cloneKeyboardEvent(event) {
  return {
    type: event.type,
    key: event.key,
    code: event.code,
    keyCode: event.keyCode,
    repeat: event.repeat,
    ctrlKey: event.ctrlKey,
    shiftKey: event.shiftKey,
    altKey: event.altKey,
    metaKey: event.metaKey,
    isTrusted: event.isTrusted,
  };
}

export function throttle(func, limit) {
  let wait = false;

  return (...args) => {
    if (!wait) {
      requestAnimationFrame(() => func(...args));
      wait = true;
      setTimeout(() => {
        wait = false;
      }, limit);
    }
  };
}

// `throttleKeyDown` waits for a maximum of `limit` ms after a keydown event.
// If no keyup event arrives within that time, it emits the keydown with repeat=true
// to mark the start of a long press. Otherwise, it emits keydown with repeat=false.
// keyUp events are always sent immediately when they occur.
// it only accepts functions with either args like (event, command)
// or just (event)
// It's designed specifically for key press events handling in Webrtc.js
export function throttleKeyDown(func, limit) {
  const pendingKeys = new Map(); // Stores { timeoutId, event, args }

  return (...args) => {
    // event is always the first argument, command is optional second argument
    const event = args[0];
    const command = args[1];

    if (!event || !event.type) {
      console.warn("throttleKeyDown: Invalid event", event);
      return;
    }

    // Use command if available, otherwise use event.key to identify the specific key
    const keyIdentifier = command || event.key;

    if (event.type === "keydown") {
      // If this key is already pending, ignore subsequent keydown events (key repeat)
      if (pendingKeys.has(keyIdentifier)) {
        return;
      }

      // Clone the event now since KeyboardEvent properties become inaccessible after the event is handled
      const clonedEvent = cloneKeyboardEvent(event);

      // Start a timer to emit the keydown with repeat=true if no keyup arrives
      const timeoutId = setTimeout(() => {
        // No keyup received within limit, this is a long press
        // Create a modified event with repeat=true
        const modifiedArgs = [...args];
        const modifiedEvent = { ...clonedEvent, repeat: true };
        modifiedArgs[0] = modifiedEvent;

        func(...modifiedArgs);
        pendingKeys.delete(keyIdentifier);
      }, limit);

      pendingKeys.set(keyIdentifier, { timeoutId, event: clonedEvent, args });
    } else if (event.type === "keyup") {
      const pending = pendingKeys.get(keyIdentifier);
      // Clone the keyup event immediately
      const clonedKeyupEvent = cloneKeyboardEvent(event);

      if (pending) {
        // Keyup arrived before timeout, cancel the timer and send both events
        clearTimeout(pending.timeoutId);
        func(...pending.args);
      }

      // Always send the keyup event
      const keyupArgs = [...args];
      keyupArgs[0] = clonedKeyupEvent;
      func(...keyupArgs);

      // Always clean up pending state
      pendingKeys.delete(keyIdentifier);
    }
  };
}
